import { useCallback, useState } from 'react';

type StateUpdater<T> = T | ((prev: T) => T);

function readPersistedState<T>(
  key: string,
  fallback: T,
  normalize?: (value: unknown) => T
): T {
  if (typeof window === 'undefined') return fallback;

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    return normalize ? normalize(parsed) : (parsed as T);
  } catch {
    return fallback;
  }
}

function writePersistedState<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / private-mode errors. UI state must never break the feature.
  }
}

export function usePersistedUiState<T>(
  key: string,
  fallback: T,
  normalize?: (value: unknown) => T
) {
  const [state, setState] = useState<T>(() =>
    readPersistedState(key, fallback, normalize)
  );

  const setPersistedState = useCallback(
    (next: StateUpdater<T>) => {
      setState(prev => {
        const value =
          typeof next === 'function' ? (next as (current: T) => T)(prev) : next;
        writePersistedState(key, value);
        return value;
      });
    },
    [key]
  );

  return [state, setPersistedState] as const;
}
